import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Demanda, User } from '../../../models';
import { receivedDemandsService } from '../../../services/receivedDemands';
import { demandaService } from '../../../services/demanda';
import type { DemandType } from '../components/navigation';
import type { SortingState } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useEventListener, useEventEmitter } from '../../../hooks/useEventBus';

type CacheKeyType = "received" | "sent" | "watching" | "toAssign" | "all" | "waitingAuth" | "inProgress" | "solved" | "finished";

type DemandaStatus = 'EMATRIBUICAO' | 'RECEBIDA' | 'ATRIBUIDA' | 'EMPROGRESSO' | 'AGUARDANDOAUTORIZACAO' | 'SOLUCIONADA' | 'FINALIZADA' | 'DEVOLVIDA' | 'ERRADA' | 'RECUSADA' | 'EXCLUIDA';
import { useSmartRefresh } from '../../../hooks/useSmartRefresh';
import {
  useDemandasQuery,
  getDemandTypeCacheKey,
} from '../hooks/queries/useDemandasQuery';
import { useDemandasAutorizacaoQuery } from '../hooks/queries/useDemandasAutorizacaoQuery';
import { useDemandFiltersQuery } from '../hooks/queries/useDemandFiltersQuery';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsGestorDoSetorLogado } from '../../../hooks/useIsGestorDoSetorLogado';

type DemandWithMeta = Demanda & { __meta?: Record<string, unknown> };

export const useUnifiedDemandsLogic = (currentType: DemandType) => {
  const navigate = useNavigate();
  const { emit } = useEventEmitter();
  const queryClient = useQueryClient();
  const { user: _user } = useAuth();
  const { isGestor, isAdmin, setor: setorLogado } = useIsGestorDoSetorLogado();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [demandanteIds, setDemandanteIds] = useState<(string | number)[]>([]);
  const [demandadoIds, setDemandadoIds] = useState<(string | number)[]>([]);
  const [processoId, setProcessoId] = useState<string>('');
  const [createdStart, setCreatedStart] = useState<string>('');
  const [createdEnd, setCreatedEnd] = useState<string>('');
  const [deadlineStart, setDeadlineStart] = useState<string>('');
  const [deadlineEnd, setDeadlineEnd] = useState<string>('');
  const [loadingStatusChange, setLoadingStatusChange] = useState<Record<number, boolean>>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [filtersLoaded, setFiltersLoaded] = useState(false); // 🚀 Controla se filtros foram carregados

  // 🚀 Montar objeto de filtros para enviar ao backend (server-side filtering)
  // Usamos spread condicional para omitir propriedades vazias (compatível com exactOptionalPropertyTypes)
  const serverFilters = useMemo(() => {
    const filters: {
      buscar?: string;
      status?: string;
      prioridade?: string;
      processo_fk?: string;
    } = {};
    
    if (searchTerm.trim()) filters.buscar = searchTerm.trim();
    if (statusFilter) filters.status = statusFilter;
    if (priorityFilter) filters.prioridade = priorityFilter;
    if (processoId) filters.processo_fk = processoId;
    
    return filters;
  }, [searchTerm, statusFilter, priorityFilter, processoId]);

  // Query para demandas normais (não autorizações) - agora com filtros server-side
  const demandasQueryResult = useDemandasQuery(currentType, {
    enabled: currentType !== 'authorization', // ✅ Desabilita para authorization 
    filters: serverFilters, // 🚀 Passa filtros para o backend
  });

  // Query específica para autorizações (usa /me/autorizacoes)
  const autorizacaoQueryResult = useDemandasAutorizacaoQuery({
    enabled: currentType === 'authorization', // ✅ Habilita APENAS para authorization
  });

  // Selecionar o resultado correto baseado no tipo
  const {
    data: demandasData = [],
    isLoading: isLoadingDemandas,
    isFetching: isFetchingDemandas,
    error: demandasError,
    cacheKey,
    queryKey,
    forceRefresh,
    dataUpdatedAt,
  } = currentType === 'authorization' ? autorizacaoQueryResult : demandasQueryResult;

  const {
    data: filtersData,
    error: filtersError,
    refetch: refetchFilters,
  } = useDemandFiltersQuery({
    enabled: false, // 🚀 Não carregar automaticamente
  });

  // Usar useMemo para evitar re-renders desnecessários
  const userOptions = useMemo(() => {
    return filtersData?.userOptions ?? [];
  }, [filtersData?.userOptions]);
  
  const processoOptions = useMemo(() => {
    return filtersData?.processoOptions ?? [];
  }, [filtersData?.processoOptions]);

  // Função para carregar filtros quando necessário
  const loadFiltersIfNeeded = useCallback(async () => {
    if (!filtersLoaded && !filtersData) {
      await refetchFilters();
      setFiltersLoaded(true);
    }
  }, [filtersLoaded, filtersData, refetchFilters]);

  const userLookup = useMemo(() => {
    const map = new Map<number, User>();

    userOptions.forEach((option) => {
      const id = Number(option.value);
      if (!Number.isFinite(id)) {
        return;
      }

      const name = option.name ?? option.label ?? `Usuário #${id}`;

      map.set(id, {
        id,
        name,
        email: option.email ?? '',
        funcao_usuario: 'DESCONHECIDO',
        cargo_usuario: 'DESCONHECIDO',
      });
    });

    return map;
  }, [userOptions]);

  const processoLookup = useMemo(() => {
    const map = new Map<number, string>();

    processoOptions.forEach((option) => {
      const id = Number(option.value);
      if (!Number.isFinite(id)) {
        return;
      }

      map.set(id, option.label);
    });

    return map;
  }, [processoOptions]);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'id', desc: true }]);

  const loading = isLoadingDemandas || (isFetchingDemandas && !isLoadingDemandas);
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  const error =
    mutationError ??
    (demandasError instanceof Error ? demandasError.message : null) ??
    (filtersError instanceof Error ? filtersError.message : null);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
    setDemandanteIds([]);
    setDemandadoIds([]);
    setProcessoId('');
    setCreatedStart('');
    setCreatedEnd('');
    setDeadlineStart('');
    setDeadlineEnd('');
    setCurrentPage(1);
    setMutationError(null);
  }, []);

  const applySorting = (data: Demanda[]): Demanda[] => {
    if (!sorting.length) return data;
    const { id: sortKey, desc } = sorting[0];

    return [...data].sort((a, b) => {
      let aVal: string | number | undefined;
      let bVal: string | number | undefined;

      switch (sortKey) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'titulo':
          aVal = a.titulo?.toLowerCase() || '';
          bVal = b.titulo?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'prioridade': {
          const priorityOrder = { Urgente: 4, Alta: 3, Media: 2, Baixa: 1 };
          aVal = priorityOrder[a.prioridade as keyof typeof priorityOrder] || 0;
          bVal = priorityOrder[b.prioridade as keyof typeof priorityOrder] || 0;
          break;
        }
        case 'demandante':
          aVal = a.demandante?.name?.toLowerCase() || '';
          bVal = b.demandante?.name?.toLowerCase() || '';
          break;
        case 'previsao_entrega':
          aVal = a.previsao_entrega ? new Date(a.previsao_entrega).getTime() : 0;
          bVal = b.previsao_entrega ? new Date(b.previsao_entrega).getTime() : 0;
          break;
        case 'setores':
          aVal = (Array.isArray(a.setores) && a.setores.length > 0
            ? a.setores.map((s) => s.nome_setor).join(', ')
            : (a.setor?.nome_setor ?? '')).toLowerCase();
          bVal = (Array.isArray(b.setores) && b.setores.length > 0
            ? b.setores.map((s) => s.nome_setor).join(', ')
            : (b.setor?.nome_setor ?? '')).toLowerCase();
          break;
        case 'demandado':
          aVal = a.demandado?.name?.toLowerCase() || '';
          bVal = b.demandado?.name?.toLowerCase() || '';
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (aVal === bVal) return 0;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }

      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return desc ? 1 : -1;
      if (bVal === undefined) return desc ? -1 : 1;

      const result = aVal > bVal ? 1 : -1;
      return desc ? -result : result;
    });
  };

  const normalizedDemandas = useMemo<Demanda[]>(() => {
    return (demandasData ?? []).map((demanda) => {
      const normalized = { ...demanda } as DemandWithMeta;
      const mutable = normalized as unknown as Record<string, unknown>;

      const authorizationMeta = (normalized.__meta as { autorizacao?: { processo?: unknown } } | undefined)?.autorizacao;
      const processoFromAuthorization = authorizationMeta?.processo;

      if (!normalized.processo && processoFromAuthorization) {
        mutable.processo = {
          ...(normalized.processo ?? {}),
          ...processoFromAuthorization,
        };
      }

      if (normalized.processo_fk && (!normalized.processo || !normalized.processo.nome_processo)) {
        const processoName = processoLookup.get(Number(normalized.processo_fk));
        if (processoName) {
          mutable.processo = {
            ...(normalized.processo ?? {}),
            id: Number(normalized.processo_fk),
            nome_processo: processoName,
          };
        }
      }

      if (normalized.processo?.setor == null && normalized.setor) {
        mutable.processo = {
          ...(normalized.processo ?? {}),
          setor: normalized.setor,
        };
      }

      if (!normalized.demandante && normalized.demandante_fk) {
        const fallbackDemandante = userLookup.get(Number(normalized.demandante_fk));
        if (fallbackDemandante) {
          normalized.demandante = fallbackDemandante;
        }
      }

      const activeAssignments = (normalized.atribuidas ?? [])
        .filter((attr) => attr && attr.active !== false)
        .map((attr) => attr.user ?? userLookup.get(Number(attr.usuario_fk)))
        .filter(Boolean) as User[];

      const demandadosRelation = Array.isArray((normalized as Demanda & { demandados?: User[] }).demandados)
        ? ((normalized as Demanda & { demandados?: User[] }).demandados as User[])
        : [];

      const resolvedDemandados = activeAssignments.length > 0 ? activeAssignments : demandadosRelation;

      if (resolvedDemandados.length > 0) {
        normalized.demandados = resolvedDemandados;
        normalized.demandado = resolvedDemandados[0];
      } else if (!normalized.demandado && normalized.demandado_fk) {
        const fallbackDemandado = userLookup.get(Number(normalized.demandado_fk));
        if (fallbackDemandado) {
          normalized.demandado = fallbackDemandado;
          normalized.demandados = [fallbackDemandado];
        }
      }

      return normalized;
    });
  }, [demandasData, processoLookup, userLookup]);

  // Se o usuário for GESTOR do setor logado, expandir a lista de demandas
  // para incluir todas as demandas do setor (não apenas as dele)
  const allDemandas = useMemo(() => {
    // Para gestores, não precisamos modificar - o backend já retorna todas as demandas do setor
    // Esta lógica é apenas documentação do comportamento esperado:
    // - GESTOR/ADMIN em 'received': vê todas as demandas recebidas no setor
    // - GESTOR/ADMIN em 'sent': vê todas as demandas enviadas pelo setor
    // - GESTOR/ADMIN em 'toAssign': vê todas as demandas em atribuição no setor
    
    if ((isGestor || isAdmin) && setorLogado) {
      // Admin or manager viewing demands from their sector
    }
    
    // Para authorization, não precisa filtrar - já vem filtrado do backend via /me/autorizacoes
    // O backend retorna apenas demandas com status AGUARDANDOAUTORIZACAO
    
    return normalizedDemandas;
  }, [normalizedDemandas, isGestor, isAdmin, setorLogado]);

  // 🚀 Filtragem agora é server-side para os filtros principais (buscar, status, prioridade, processo)
  // Apenas filtros adicionais que não são suportados pelo backend são aplicados no frontend
  const filteredDemandas = allDemandas.filter((demanda) => {
    // Filtros server-side: buscar, status, prioridade, processo_fk
    // Esses já foram aplicados pelo backend, não precisamos filtrar novamente

    // Filtros client-side (não suportados pelo backend):
    const matchesDemandante =
      demandanteIds.length === 0 ||
      demandanteIds.some((id) => String(demanda.demandante?.id) === String(id));
    
    const matchesDemandado =
      demandadoIds.length === 0 ||
      demandadoIds.some((id) => {
        if (Array.isArray(demanda.demandados) && demanda.demandados.length > 0) {
          return demanda.demandados.some((d: { id?: number | string }) => String(d.id) === String(id));
        }
        return String(demanda.demandado?.id) === String(id);
      });

    const createdAt = demanda.created_at ? new Date(demanda.created_at) : null;
    const inCreatedRange =
      (!createdStart || (createdAt && createdAt >= new Date(createdStart))) &&
      (!createdEnd || (createdAt && createdAt <= new Date(`${createdEnd}T23:59:59`)));

    const deadlineDate = demanda.previsao_entrega ? new Date(demanda.previsao_entrega) : null;
    const inDeadlineRange =
      (!deadlineStart || (deadlineDate && deadlineDate >= new Date(deadlineStart))) &&
      (!deadlineEnd || (deadlineDate && deadlineDate <= new Date(`${deadlineEnd}T23:59:59`)));

    return (
      matchesDemandante &&
      matchesDemandado &&
      inCreatedRange &&
      inDeadlineRange
    );
  });

  const sortedDemandas = applySorting(filteredDemandas);
  const totalItems = sortedDemandas.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDemandas = sortedDemandas.slice(startIndex, endIndex);

  const stats = {
    total: allDemandas.length,
    emAtribuicao: allDemandas.filter((d) => d.status === 'EMATRIBUICAO').length,
    atribuidas: allDemandas.filter((d) => d.status === 'ATRIBUIDA').length,
    emProgresso: allDemandas.filter((d) => d.status === 'EMPROGRESSO').length,
    aguardandoAutorizacao: allDemandas.filter((d) => d.status === 'AGUARDANDOAUTORIZACAO').length,
    solucionadas: allDemandas.filter((d) => d.status === 'SOLUCIONADA').length,
    finalizadas: allDemandas.filter((d) => d.status === 'FINALIZADA').length,
    devolvidas: allDemandas.filter((d) => d.status === 'DEVOLVIDA').length,
    erradas: allDemandas.filter((d) => d.status === 'ERRADA').length,
    recusadas: allDemandas.filter((d) => d.status === 'RECUSADA').length,
    excluidas: allDemandas.filter((d) => d.status === 'EXCLUIDA').length,
  };

  const handleView = (demanda: Demanda) => {
    navigate(`/demands/${demanda.id}/view?type=all`);
  };

  const handleEdit = (_demanda: Demanda) => {
    // Edit demand functionality
  };

  const handleStatusChange = (_demanda: Demanda, _newStatus: string) => {
    // Change demand status functionality
  };

  const handleStatusChangeById = async (demandaId: number, newStatus: string) => {
    try {
      setLoadingStatusChange((prev) => ({ ...prev, [demandaId]: true }));
      setMutationError(null);

      const validStatuses = [
        'EMATRIBUICAO',
        'ATRIBUIDA',
        'EMPROGRESSO',
        'AGUARDANDOAUTORIZACAO',
        'SOLUCIONADA',
        'FINALIZADA',
        'DEVOLVIDA',
        'ERRADA',
        'RECUSADA',
        'EXCLUIDA',
      ];

      if (!validStatuses.includes(newStatus)) {
        throw new Error(`Status inválido: ${newStatus}`);
      }

      const currentDemandas = queryClient.getQueryData<Demanda[]>(queryKey) ?? [];
      const demandaAtual = currentDemandas.find((d) => d.id === demandaId);
      const oldStatus = demandaAtual?.status || '';

      await demandaService.update(demandaId, { status: newStatus as DemandaStatus });

      let demandaAtualizada: Demanda | undefined;
      queryClient.setQueryData<Demanda[]>(queryKey, (prev = []) =>
        prev.map((demanda) => {
          if (demanda.id === demandaId) {
            demandaAtualizada = { ...demanda, status: newStatus as DemandaStatus };
            return demandaAtualizada;
          }
          return demanda;
        })
      );

      if (cacheKey) {
        receivedDemandsService.invalidateCache(cacheKey as CacheKeyType);
      }
      queryClient.invalidateQueries({ queryKey: ['unified-demandas'], refetchType: 'inactive' });

      emit('demand-status-changed', {
        demandId: demandaId,
        oldStatus,
        newStatus,
        demanda: demandaAtualizada,
      });

      // Status changed successfully
    } catch (err) {
      console.error('❌ Erro ao alterar status da demanda:', err);
      setMutationError(err instanceof Error ? err.message : 'Erro ao alterar status da demanda');
    } finally {
      setLoadingStatusChange((prev) => {
        const newState = { ...prev };
        delete newState[demandaId];
        return newState;
      });
    }
  };

  const manualRefresh = useCallback(async () => {
    setMutationError(null);
    await forceRefresh();
  }, [forceRefresh]);

  const smartRefresh = useSmartRefresh({
    onRefresh: manualRefresh,
    autoRefreshOnEvents: true,
    showToastOnRefresh: false,
  });

  const autoRefresh = useAutoRefresh({
    interval: 30 * 1000, // 30 segundos
    enabled: true,
    onRefresh: useCallback(async () => {
      await forceRefresh();
    }, [forceRefresh]),
    minInterval: 30 * 1000, // 30 segundos mínimo
    maxInterval: 30 * 1000, // 30 segundos máximo
  });

  useEventListener(
    'cache-invalidated',
    ({ cacheKeys }) => {
      // Check if refresh is needed based on cache keys
      const typeKey = getDemandTypeCacheKey(currentType);
      if (cacheKeys.some((key) => key.includes(typeKey) || key.includes('all'))) {
        forceRefresh();
      }
    },
    [currentType, forceRefresh]
  );

  useEffect(() => {
    clearFilters();
  }, [currentType, clearFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    priorityFilter,
    demandanteIds,
    demandadoIds,
    processoId,
    createdStart,
    createdEnd,
    deadlineStart,
    deadlineEnd,
  ]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return {
    demandas: paginatedDemandas,
    loading,
    error,
    stats,
    loadingStatusChange,
    lastUpdated,
    autoRefresh,
    manualRefresh: smartRefresh.refresh,
    smartRefresh,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    sorting,
    setSorting,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    userOptions,
    processoOptions,
    demandanteIds,
    setDemandanteIds,
    demandadoIds,
    setDemandadoIds,
    processoId,
    setProcessoId,
    createdStart,
    setCreatedStart,
    createdEnd,
    setCreatedEnd,
    deadlineStart,
    setDeadlineStart,
    deadlineEnd,
    setDeadlineEnd,
    handleView,
    handleEdit,
    handleStatusChange,
    handleStatusChangeById,
    clearFilters,
    // 🚀 Nova função para carregar filtros sob demanda
    loadFiltersIfNeeded,
    filtersLoaded,
  };
};
